import type { ReactNode } from 'react'

import { datum } from '../format'

/** Werte eines Werkzeugaufrufs oder -ergebnisses lesbar: Feldnamen in Worten, Zahlen deutsch, Tage als
 * TT.MM.JJJJ, verschachtelte Sätze als Tabelle - statt des rohen JSON (Wunsch vom 18.09.2026). */

const FELDNAMEN: Record<string, string> = {
  artikelnummer: 'Artikelnummer',
  bezeichnung: 'Bezeichnung',
  kurzname: 'Kurzname',
  art: 'Art',
  einheit: 'Einheit',
  gebindegruppe: 'Blatt',
  gruppe: 'Blatt',
  warengruppe: 'Warengruppe',
  hersteller: 'Hersteller',
  lieferant: 'Lieferant',
  lieferant_id: 'Lieferant (ID)',
  mvn: 'MVN (Stück je t)',
  mvn_begruendung: 'Begründung der MVN',
  mindestbestand_t: 'Mindestbestand (t)',
  plan_t_je_monat: 'Planmenge (t je Monat)',
  lieferzeit_tage: 'Lieferzeit (Tage)',
  layout_vorlauf_tage: 'Vorlauf Layout (Tage)',
  stueck_je_palette: 'Stück je Palette',
  gebindegewicht_kg: 'Gebindegewicht (kg)',
  paar_gruppe: 'Paar (Boden und Deckel)',
  vorstufen: 'Vorstufen',
  vorstufen_tage: 'Vorstufen (Tage)',
  aktiv: 'aktiv',
  material: 'Material',
  fertigartikel: 'Fertigartikel',
  menge_t: 'Menge (t)',
  menge: 'Menge',
  menge_original: 'Menge laut Beleg',
  einheit_original: 'Einheit laut Beleg',
  status: 'Status',
  liefer_tag: 'Liefertag',
  liefer_kw: 'Liefer-KW',
  beleg: 'Beleg',
  bemerkung: 'Bemerkung',
  grund: 'Grund',
  seit: 'seit',
  tag: 'Tag',
  stichtag: 'Stichtag',
  von: 'von',
  bis: 'bis',
  jahr: 'Jahr',
  monat: 'Monat',
  text: 'Text',
  benutzer: 'Benutzer',
  id: 'ID',
  lauf_id: 'Lauf',
  import_lauf_id: 'Importlauf',
  mindestabruf_paletten: 'Mindestabruf (Paletten)',
  abruf_vorlauf_tage: 'Vorlauf Abruf (Tage)',
  abruf_puffer_tage: 'Puffer Abruf (Tage)',
  puffer_tage: 'Puffer Nachauflage (Tage)',
  produktionszeit_tage: 'Produktionszeit (Tage)',
  abruffrist_monate: 'Abruffrist (Monate)',
  preis_gilt: 'Preis gilt bei',
  artikel: 'Artikel',
  staffeln: 'Preisstaffeln',
  angelegt_am: 'angelegt am',
  erledigt_am: 'erledigt am',
  angelegt_von: 'angelegt von',
  koerper: 'Angaben',
  hinweise: 'Hinweise',
  warnungen: 'Warnungen',
}

/** „mindestbestand_t" wird „Mindestbestand (t)", ein unbekanntes „foo_bar_kw" wird „Foo bar (KW)". */
export function feldname(schluessel: string): string {
  if (FELDNAMEN[schluessel]) return FELDNAMEN[schluessel]
  let s = schluessel
  const einheiten: [RegExp, string][] = [[/_t$/, ' (t)'], [/_tage$/, ' (Tage)'], [/_kw$/, ' (KW)'], [/_kg$/, ' (kg)'], [/_id$/, ' (ID)'], [/_am$/, ' am'], [/_monate$/, ' (Monate)']]
  let anhang = ''
  for (const [muster, ersatz] of einheiten) {
    if (muster.test(s)) {
      s = s.replace(muster, '')
      anhang = ersatz
      break
    }
  }
  s = s.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1) + anhang
}

function istSatz(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === 'object' && wert !== null && !Array.isArray(wert)
}

export function wertText(wert: unknown): string {
  if (wert === null || wert === undefined || wert === '') return '–'
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein'
  if (typeof wert === 'number') return Number.isInteger(wert) && Math.abs(wert) < 100000 ? String(wert) : wert.toLocaleString('de-DE', { maximumFractionDigits: 3 })
  if (typeof wert === 'string') return /^\d{4}-\d{2}-\d{2}$/.test(wert) ? datum(wert) : wert
  if (Array.isArray(wert)) return wert.map(wertText).join(', ')
  return JSON.stringify(wert)
}

function Zeilen({ satz, praefix }: { satz: Record<string, unknown>; praefix?: string }): ReactNode {
  return (
    <>
      {Object.entries(satz).map(([k, v]) => {
        const name = praefix ? `${praefix} · ${feldname(k)}` : feldname(k)
        // Ein verschachtelter Satz (der Körper einer Änderung) wird flach: seine Felder stehen direkt in der Liste.
        if (istSatz(v)) return <Zeilen key={k} satz={v} praefix={k === 'koerper' ? undefined : name} />
        if (Array.isArray(v) && v.length > 0 && v.every(istSatz)) {
          return (
            <tr key={k}>
              <th>{name}</th>
              <td>
                <Saetze zeilen={v as Record<string, unknown>[]} />
              </td>
            </tr>
          )
        }
        return (
          <tr key={k}>
            <th>{name}</th>
            <td>{wertText(v)}</td>
          </tr>
        )
      })}
    </>
  )
}

/** Mehrere gleichartige Sätze (eine Liste von Bestellungen, Artikeln) als Tabelle mit Spalten. */
function Saetze({ zeilen }: { zeilen: Record<string, unknown>[] }): ReactNode {
  const spalten = Array.from(new Set(zeilen.flatMap((z) => Object.keys(z)))).filter((k) => !zeilen.every((z) => z[k] === null || z[k] === undefined))
  const sichtbar = zeilen.slice(0, 50)
  return (
    <div className="tabellenrahmen chat-tabelle">
      <table>
        <thead>
          <tr>
            {spalten.map((k) => (
              <th key={k}>{feldname(k)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sichtbar.map((z, i) => (
            <tr key={i}>
              {spalten.map((k) => (
                <td key={k}>{istSatz(z[k]) ? Object.entries(z[k] as Record<string, unknown>).map(([kk, vv]) => `${feldname(kk)}: ${wertText(vv)}`).join(', ') : wertText(z[k])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {zeilen.length > sichtbar.length && <p className="hinweis">{zeilen.length - sichtbar.length} weitere Zeilen nicht gezeigt.</p>}
    </div>
  )
}

/** Das Ergebnis des Rechenwerkzeugs: Spaltennamen und Zeilen als Listen - eine echte Tabelle mit Köpfen. */
function Abfrageergebnis({ spalten, zeilen, rest }: { spalten: string[]; zeilen: unknown[][]; rest: Record<string, unknown> }): ReactNode {
  return (
    <>
      <div className="tabellenrahmen chat-tabelle">
        <table>
          <thead>
            <tr>
              {spalten.map((k) => (
                <th key={k}>{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zeilen.slice(0, 50).map((z, i) => (
              <tr key={i}>
                {z.map((v, j) => (
                  <td key={j}>{wertText(v)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {Object.keys(rest).length > 0 && (
        <table className="werte-tabelle">
          <tbody>
            <Zeilen satz={rest} />
          </tbody>
        </table>
      )}
    </>
  )
}

/** Ein beliebiger JSON-Wert als Tabelle; ein Text bleibt Text. */
export default function WerteTabelle({ wert }: { wert: unknown }): ReactNode {
  if (istSatz(wert)) {
    if (Object.keys(wert).length === 0) return null
    const spalten = wert.spalten
    const zeilen = wert.zeilen
    if (Array.isArray(spalten) && spalten.every((k) => typeof k === 'string') && Array.isArray(zeilen) && zeilen.every(Array.isArray)) {
      const rest = Object.fromEntries(Object.entries(wert).filter(([k]) => !['spalten', 'zeilen', 'typen'].includes(k)))
      return <Abfrageergebnis spalten={spalten as string[]} zeilen={zeilen as unknown[][]} rest={rest} />
    }
    return (
      <table className="werte-tabelle">
        <tbody>
          <Zeilen satz={wert} />
        </tbody>
      </table>
    )
  }
  if (Array.isArray(wert)) {
    if (wert.length === 0) return <p className="hinweis">keine Einträge</p>
    if (wert.every(istSatz)) return <Saetze zeilen={wert as Record<string, unknown>[]} />
    return <p>{wertText(wert)}</p>
  }
  return <pre className="werkzeug-ergebnis">{String(wert)}</pre>
}

/** Ein Werkzeugergebnis ist meist JSON - dann als Tabelle; sonst bleibt der Text, wie er ist. */
export function ergebnisLesen(text: string): unknown {
  const t = text.trim()
  if (!(t.startsWith('{') || t.startsWith('['))) return text
  try {
    return JSON.parse(t)
  } catch {
    return text
  }
}
